import { inject } from '@angular/core';
import { type ActivatedRouteSnapshot, type CanActivateFn, Router } from '@angular/router';

import { type ParticipantModel } from '../../models/Participant';
import { type ContestRole, SystemRole } from '../../models/User';
import { AuthService } from '../services/auth.service';
import { ContextService } from '../services/context.service';
import { SessionSyncService } from '../services/session-sync.service';
import { StorageService } from '../services/storage.service';

/**
 * Checks if the user has one of the required system roles.
 */
const checkSystemRoles = (
  route: ActivatedRouteSnapshot,
  currentSystemRole: SystemRole,
  router: Router,
): boolean | ReturnType<Router['createUrlTree']> => {
  const roles: SystemRole[] = route.data['roles'] as SystemRole[];

  // If roles are specified and the user doesn't have any of them
  if (roles && !roles.includes(currentSystemRole)) {
    console.warn(`Access denied for role ${currentSystemRole}. Required: ${roles}`);
    return router.createUrlTree(['']);
  }

  return true;
};

/**
 * Extracts contestId from the route hierarchy.
 */
const getContestId = (route: ActivatedRouteSnapshot): string | undefined => {
  return (
    route.pathFromRoot
      .reverse()
      .find((s) => s.paramMap.has('contestId'))
      ?.paramMap.get('contestId') ?? undefined
  );
};

/**
 * Checks if any of the participants has one of the required roles for the given contest.
 */
const hasRequiredRole = (
  participants: ParticipantModel[],
  contestId: string,
  requiredRoles: ContestRole[],
): boolean => {
  return participants.some(
    (p) => String(p.contestId) === String(contestId) && requiredRoles.includes(p.contestRole),
  );
};

/**
 * Checks if the user has one of the required contest roles for a specific contest.
 */
const checkContestRoles = (
  route: ActivatedRouteSnapshot,
  authService: AuthService,
  contextService: ContextService,
  storageService: StorageService,
  router: Router,
): boolean | ReturnType<Router['createUrlTree']> => {
  const requiredContestRoles: ContestRole[] = route.data['contestRoles'] as ContestRole[];
  if (!requiredContestRoles || requiredContestRoles.length === 0) {
    return true;
  }

  const contestId = getContestId(route);
  if (contestId) {
    const participants: ParticipantModel[] =
      contextService.availableParticipants().length > 0
        ? contextService.availableParticipants()
        : (storageService.getParticipants() ?? []);

    if (hasRequiredRole(participants, contestId, requiredContestRoles)) {
      return true;
    }
  }

  console.warn(
    `Access denied for user ${authService.getUserId()} on contest ${contestId}. Required roles: ${requiredContestRoles}`,
  );
  return router.createUrlTree(['']);
};

export const userRoleGuard: CanActivateFn = async (route, state) => {
  const authService: AuthService = inject(AuthService);
  const sessionSyncService = inject(SessionSyncService);
  const contextService: ContextService = inject(ContextService);
  const storageService = inject(StorageService);
  const router: Router = inject(Router);

  // Wait for session synchronization if it's currently in progress
  await sessionSyncService.waitForSync();

  const currentSystemRole: SystemRole | null = authService.getSystemRole();

  if (currentSystemRole == null) {
    // Not logged in, redirect to login with return URL
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  const systemRoleCheck = checkSystemRoles(route, currentSystemRole, router);
  if (systemRoleCheck !== true) {
    return systemRoleCheck;
  }

  // If it's a regular user, we might need to check for specific contest role
  if (currentSystemRole === SystemRole.User) {
    return checkContestRoles(route, authService, contextService, storageService, router);
  }

  return true;
};

export const redirectToRoleRoute: CanActivateFn = () => {
  const authService: AuthService = inject(AuthService);
  const router = inject(Router);

  if (authService.isConnected()) {
    const currentSystemRole: SystemRole | null = authService.getSystemRole();

    switch (currentSystemRole) {
      case SystemRole.SuperAdmin:
        return router.createUrlTree(['admin']);
      case SystemRole.Admin:
      case SystemRole.User:
        return router.createUrlTree(['contests']);
      default:
        // If connected but role is unknown, maybe we need to fetch user data
        // or just showing HomeComponent is fine as a fallback
        return true;
    }
  }

  return true;
};
